import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  CircleCheck,
  LockKeyhole,
  MessageCircle,
} from 'lucide-react';
import { StudioProducts } from '../components/StudioProducts';

const principles = [
  {
    number: '01',
    title: 'Useful before clever',
    text: 'Every feature has a job. If it does not make the work clearer, safer, or faster, it does not ship.',
  },
  {
    number: '02',
    title: 'Your data stays yours',
    text: 'Local-first where it matters, and no account required unless it creates real value.',
  },
  {
    number: '03',
    title: 'Limits, clearly stated',
    text: 'Compatibility and tradeoffs are documented plainly. Trust starts with saying what a product cannot do.',
  },
];

export function HomePage() {
  return (
    <>
      <main id="content" tabIndex={-1}>
        <section className="hero shell">
          <div className="hero-copy">
            <p className="eyebrow">
              <span className="status-dot" aria-hidden="true" />
              Independent software studio
            </p>
            <h1>
              Serious software.
              <br />
              <span>Built with care.</span>
            </h1>
            <p className="hero-intro">
              alxnsv.io builds focused tools for people who care about keeping
              their work accurate—and their data their own.
            </p>
            <div className="hero-actions">
              <a className="button button-dark" href="#projects">
                Explore the work
                <ArrowDownRight size={18} strokeWidth={2} aria-hidden="true" />
              </a>
              <a className="text-link" href="/spoolmark/blog">
                Read Spoolmark journal
                <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
              </a>
            </div>
          </div>

          <aside className="hero-note" aria-label="Studio note">
            <span className="note-index">A / 01</span>
            <p>
              Software earns trust in small moments: a clear warning, an honest
              limitation, a detail remembered.
            </p>
            <span className="hand-signature">— Alex</span>
          </aside>
        </section>

        <section className="trust-line" aria-label="Product commitments">
          <div className="shell trust-line-inner">
            <a href="#projects">
              <CircleCheck size={16} aria-hidden="true" />
              <span>
                <strong>Focused tools</strong>
                <small>Built for a clear purpose</small>
              </span>
            </a>
            <a href="#principles">
              <LockKeyhole size={15} aria-hidden="true" />
              <span>
                <strong>Data stays yours</strong>
                <small>Clear choices and controls</small>
              </span>
            </a>
            <a href="#about">
              <MessageCircle size={16} aria-hidden="true" />
              <span>
                <strong>Direct support</strong>
                <small>Talk to the maker</small>
              </span>
            </a>
          </div>
        </section>

        <section className="projects-section shell" id="projects">
          <div className="section-heading">
            <p className="eyebrow">Selected projects</p>
            <p className="section-count">02 / growing</p>
          </div>

          <StudioProducts />
        </section>

        <section className="principles-section shell" id="principles">
          <div className="principles-intro">
            <p className="eyebrow">The standard</p>
            <h2>Quality is a behavior.</h2>
            <p>
              Not a badge added at the end. These are the working principles
              behind every product carrying the alxnsv.io name.
            </p>
          </div>

          <div className="principles-list">
            {principles.map((principle) => (
              <article className="principle" key={principle.number}>
                <span>{principle.number}</span>
                <div>
                  <h3>{principle.title}</h3>
                  <p>{principle.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="about-section shell" id="about">
          <div className="about-mark" aria-hidden="true">
            AN
          </div>
          <div className="about-copy">
            <p className="eyebrow">The person behind the label</p>
            <h2>A name behind every release.</h2>
            <p>
              alxnsv.io is the independent product studio of Alex Nosov. Every
              product decision, support reply, and build note comes from the
              same place—one maker accountable for the details.
            </p>
            <div className="about-actions">
              <a className="button button-dark" href="mailto:hello@alxnsv.io">
                Say hello
                <ArrowUpRight size={17} aria-hidden="true" />
              </a>
              <a className="text-link" href="/spoolmark/blog">
                Read the build notes
                <ArrowRight size={16} aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
